import { useEffect, useRef, useState } from "react";

/**
 * Soft, ambient click effect — a gentle cluster of spore-like orbs
 * drifting outward from the cursor. Decorative only (pointer-events-none).
 */
export default function MoldClickFx() {
  const [splats, setSplats] = useState([]);
  const idRef = useRef(0);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      const id = ++idRef.current;
      const x = e.clientX;
      const y = e.clientY;
      const spores = Array.from({ length: 8 }).map((_, i) => {
        const angle = (i / 8) * Math.PI * 2 + Math.random() * 0.6;
        const dist = 32 + Math.random() * 48;
        return {
          k: i,
          dx: Math.round(Math.cos(angle) * dist),
          dy: Math.round(Math.sin(angle) * dist),
          size: 8 + Math.random() * 14,
          delay: Math.round(Math.random() * 220),
          hue: Math.random() > 0.55 ? "blue" : "green",
        };
      });
      setSplats((s) => [...s, { id, x, y, spores }]);
      setTimeout(() => {
        setSplats((s) => s.filter((sp) => sp.id !== id));
      }, 1800);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none z-[60] overflow-hidden"
      data-testid="mold-click-fx"
    >
      {splats.map((s) => (
        <SoftSplat key={s.id} x={s.x} y={s.y} spores={s.spores} />
      ))}
    </div>
  );
}

function SoftSplat({ x, y, spores }) {
  return (
    <span
      className="absolute"
      style={{ left: x, top: y, transform: "translate(-50%, -50%)" }}
    >
      {/* Soft central glow */}
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 eco-soft-glow" />

      {/* Drifting spores */}
      {spores.map((s) => (
        <span
          key={s.k}
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 eco-soft-spore ${
            s.hue === "blue" ? "eco-spore-blue" : "eco-spore-green"
          }`}
          style={{
            ["--dx"]: `${s.dx}px`,
            ["--dy"]: `${s.dy}px`,
            ["--size"]: `${s.size}px`,
            animationDelay: `${s.delay}ms`,
          }}
        />
      ))}

      {/* Single gentle ripple */}
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 eco-soft-ring" />
    </span>
  );
}
